/* eslint-disable max-lines */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  FileSignature,
  FileText,
  FileUp,
  Trash2,
  Edit2,
  Plus
} from 'lucide-react';

import Button from '@components/atoms/ui-kit/button.tsx';
import { Text } from '@components/atoms/ui-kit/text';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import Input from '@components/atoms/ui-kit/input.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import PageHeader from '@components/layout/page-header.tsx';

import { useGetExportTemplates } from '@utils/api/organizations/use-get-export-templates.tsx';
import { useAddExportTemplate } from '@utils/api/organizations/use-add-export-template.tsx';
import { useUpdateExportTemplate } from '@utils/api/organizations/use-update-export-template.tsx';
import { useDeleteExportTemplate } from '@utils/api/organizations/use-delete-export-template.tsx';

import defaultPrompt, {
  defaultSummarizationPrompt
} from '@utils/types/defaultPrompt.ts';
import { Template } from '@utils/types/organization/export-template.ts';
import { BaseError, Roles } from '@utils/types/shared/base';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { TokenExtension } from '@utils/extensions/token.extension.ts';

interface OptionType {
  value: string;
  label: string;
}

const typeOptions: OptionType[] = [
  { value: 'EXPORT_TEMPLATE', label: 'EXPORT_TEMPLATE' },
  { value: 'SUMMARIZATION', label: 'SUMMARIZATION' }
];

const getDefaultPromptFor = (type: string) =>
  type === 'SUMMARIZATION' ? defaultSummarizationPrompt : defaultPrompt;

type FormState = {
  name: string;
  template_type: string;
  prompt: string | null;
  template_docx: File | null;
  template_example: File | null;
};

const initialFormState: FormState = {
  name: '',
  template_type: 'EXPORT_TEMPLATE',
  prompt: getDefaultPromptFor('EXPORT_TEMPLATE'),
  template_docx: null,
  template_example: null
};

const AccountPersonalization = () => {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();

  const { data: templates } = useGetExportTemplates();
  const { mutate: deleteTemplate, isLoading: deleteIsLoading } =
    useDeleteExportTemplate();
  const { mutate: addTemplate, isLoading: addIsLoading } =
    useAddExportTemplate();
  const { mutate: updateTemplate, isLoading: updateIsLoading } =
    useUpdateExportTemplate();

  const userScopes = TokenExtension.get()?.decoded?.scopes || [];
  const isAdmin = userScopes.includes(Roles.ORGADMIN);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormData, setAddFormData] = useState<FormState>(initialFormState);

  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editFormData, setEditFormData] = useState<FormState | null>(null);

  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    template_docx?: string;
    template_example?: string;
  }>({});

  const truncateText = (text: string, maxLength: number) =>
    text.length <= maxLength ? text : text.substring(0, maxLength) + '...';

  const openAddModal = () => {
    setShowAddModal(true);
    setValidationErrors({});
    setAddFormData(initialFormState);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setValidationErrors({});
    setAddFormData(initialFormState);
  };

  const validateForm = (form: FormState) => {
    const errors: typeof validationErrors = {};
    if (!form.name.trim()) {
      errors.name = t('template-name-required') || 'Template name is required';
    }
    if (form.template_type === 'EXPORT_TEMPLATE') {
      if (!form.template_docx) {
        errors.template_docx =
          t('export-template-requires-docx') ||
          'Export Template requires a DOCX template file';
      }
      if (!form.template_example) {
        errors.template_example =
          t('export-template-requires-example') ||
          'Export Template requires an example PDF file';
      }
    }
    return errors;
  };

  const handleSaveNewTemplate = () => {
    const errors = validateForm(addFormData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});

    addTemplate(
      {
        name: addFormData.name,
        template_docx: addFormData.template_docx,
        template_example: addFormData.template_example,
        template_type: addFormData.template_type,
        prompt: addFormData.prompt
      },
      {
        onSuccess: () => {
          toast.success(t('export-template-updated-successfully'));
          closeAddModal();
        },
        onError: (error: AxiosError<BaseError>) => {
          const errorMessage =
            error.response?.data?.error_description ||
            error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t('failed-to-update-the-export-template');
          toast.error(String(errorMessage));
        }
      }
    );
  };

  const openEditModal = (template: Template) => {
    setEditingTemplate(template);
    setEditFormData({
      name: template.name,
      template_type: template.type || 'EXPORT_TEMPLATE',
      prompt:
        template.prompt ||
        getDefaultPromptFor(template.type || 'EXPORT_TEMPLATE'),
      template_docx: null,
      template_example: null
    });
  };

  const closeEditModal = () => {
    setEditingTemplate(null);
    setEditFormData(null);
  };

  const handleUpdateTemplate = () => {
    if (!editingTemplate || !editFormData) return;

    if (!editFormData.name.trim()) {
      toast.error(t('template-name-required') || 'Template name is required');
      return;
    }

    const toastId = toast.loading(
      t('updating-template') || 'Updating template...',
      { icon: '📝' }
    );

    updateTemplate(
      {
        id: Number(editingTemplate.id),
        data: {
          name: editFormData.name,
          template_type: editFormData.template_type,
          prompt: editFormData.prompt || '',
          template_docx: editFormData.template_docx,
          template_example: editFormData.template_example
        }
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
          toast.success(
            t('template-updated-successfully') ||
              'Template updated successfully'
          );
          closeEditModal();
        },
        onError: (error: AxiosError<BaseError>) => {
          toast.dismiss(toastId);
          const data = error?.response?.data;
          const fallbackMessage =
            t('an-error-occurred-while-updating-template') ||
            'An error occurred while updating the template';

          let messages: string[] = [];
          if (Array.isArray(data?.detail)) {
            messages = data.detail.map(
              (err: { loc?: string[]; msg: string }) => {
                const field = err.loc?.[1] ?? 'field';
                return `${field}: ${err.msg}`;
              }
            );
          } else if (data?.message) {
            messages = [data.message];
          } else if (data?.error_description) {
            messages = [data.error_description];
          } else {
            messages = [fallbackMessage];
          }
          toast.error(messages.join('\n'));
        }
      }
    );
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return;
    deleteTemplate(deleteTargetId, {
      onSuccess: () => {
        toast.success(t('template-deleted'));
        setDeleteTargetId(null);
      },
      onError: (error: AxiosError<BaseError>) => {
        const data = error?.response?.data;
        const fallbackMessage = t(
          'an-error-occurred-while-deleting-the-template'
        );

        let messages: string[] = [];
        if (Array.isArray(data?.detail)) {
          messages = data.detail.map((err: { loc?: string[]; msg: string }) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }
        toast.error(messages.join('\n'));
        setDeleteTargetId(null);
      }
    });
  };

  const downloadTemplate = async (
    templateId: number,
    file: 'docx' | 'pdf',
    name: string
  ) => {
    const toastId = toast.loading(t('downloading') || 'Downloading...', {
      icon: '⬇️'
    });

    try {
      const res = await fetch(
        `/organizations/export-templates/${templateId}/download?file=${file}`,
        { credentials: 'include' }
      );

      if (res.status === 409) {
        toast.dismiss(toastId);
        toast.error(
          t('template-archived') ||
            'This file is archived and cannot be downloaded yet. Please try again later.'
        );
        return;
      }

      if (res.status === 202) {
        toast.dismiss(toastId);
        toast(
          t('template-rehydrating') ||
            'This file is being prepared (rehydrating). Try again in a few minutes.',
          { icon: '⏳' }
        );
        return;
      }

      if (!res.ok) {
        toast.dismiss(toastId);
        toast.error(t('download-failed') || 'Download failed.');
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.${file}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss(toastId);
      toast.success(t('download-started') || 'Download started.');
    } catch {
      toast.dismiss(toastId);
      toast.error(t('download-failed') || 'Download failed.');
    }
  };

  // ---- Render helpers ----

  const renderTemplateCard = (template: Template) => {
    const pdfUrl = template.template_example?.toString() ?? '';
    const docxUrl = template.template_docx?.toString() ?? '';
    const displayType =
      template.type === 'SUMMARIZATION' ? 'AI Summary' : 'Export';
    const truncatedName = truncateText(template.name, 20);

    return (
      <div
        key={template.id}
        className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4 transition-shadow hover:shadow-card-hover"
      >
        <div className="mb-3">
          <Text
            as="h4"
            size="base"
            weight="semibold"
            truncate="clamp2"
            className="mb-2"
            title={template.name}
          >
            {truncatedName}
          </Text>
          <Text
            as="span"
            size="xs"
            weight="medium"
            className="inline-flex items-center rounded-full border border-hairline px-2.5 py-1"
          >
            {displayType}
          </Text>
        </div>

        <div className="space-y-2 mb-4 min-h-[72px]">
          <div className="flex items-center gap-2">
            <FileText size={14} style={{ color: getColour('inactiveText') }} />
            <Text
              as="span"
              size="sm"
              tone="muted"
              truncate
              className="flex-1"
              title={docxUrl || 'No DOCX file'}
            >
              {docxUrl
                ? t('template-docx') || 'Template DOCX'
                : t('no-docx-file') || 'No DOCX file'}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <FileSignature
              size={14}
              style={{ color: getColour('inactiveText') }}
            />
            <Text
              as="span"
              size="sm"
              tone="muted"
              truncate
              className="flex-1"
              title={pdfUrl || 'No PDF file'}
            >
              {pdfUrl
                ? t('example-pdf') || 'Example PDF'
                : t('no-pdf-file') || 'No PDF file'}
            </Text>
          </div>
          {template.prompt && (
            <div className="flex items-center gap-2 pt-1">
              <Text as="span" size="sm" weight="medium" tone="brand">
                ✓ {t('custom-prompt') || 'Custom Prompt'}
              </Text>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-hairline">
          <button
            type="button"
            onClick={() =>
              downloadTemplate(Number(template.id), 'docx', template.name)
            }
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-hairline px-3 text-xs font-medium hover:opacity-80 transition-opacity"
            title="Download DOCX"
          >
            <FileText size={14} />
            <span>DOCX</span>
          </button>

          <button
            type="button"
            onClick={() =>
              downloadTemplate(Number(template.id), 'pdf', template.name)
            }
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-hairline px-3 text-xs font-medium hover:opacity-80 transition-opacity"
            title="Download PDF"
          >
            <FileSignature size={14} />
            <span>PDF</span>
          </button>

          {isAdmin && (
            <button
              type="button"
              onClick={() => openEditModal(template)}
              className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-hairline px-3 text-xs font-medium hover:opacity-80 transition-opacity"
              style={{
                color: getColour('activeText'),
                backgroundColor: getBrandTint(0.08)
              }}
              title={t('edit-template') || 'Edit template'}
            >
              <Edit2 size={14} />
              <span>{t('edit') || 'Edit'}</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setDeleteTargetId(Number(template.id))}
            disabled={deleteIsLoading}
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-hairline px-3 text-xs font-medium text-red-600 dark:text-red-400 hover:opacity-80 transition-opacity disabled:opacity-50"
            title="Delete template"
          >
            <Trash2 size={14} />
            <span>{t('delete') || 'Delete'}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderUploadField = ({
    id,
    file,
    accept,
    label,
    placeholder,
    error,
    onChange
  }: {
    id: string;
    file: File | null;
    accept: string;
    label: string;
    placeholder?: string;
    error?: string;
    onChange: (file: File) => void;
  }) => (
    <div>
      <Text as="label" size="sm" weight="medium" className="block mb-1">
        {label}
      </Text>
      <label
        htmlFor={id}
        className={cn(
          'block p-3 rounded-2xl cursor-pointer border border-dashed bg-app-soft hover:opacity-90 text-sm transition-opacity',
          error ? 'border-red-400' : 'border-hairline'
        )}
      >
        <div className="flex items-center gap-2">
          <FileUp size={16} />
          <span className="font-medium">{label}</span>
        </div>
        <input
          id={id}
          type="file"
          accept={accept}
          className="hidden"
          onChange={e => {
            if (e.target.files?.[0]) onChange(e.target.files[0]);
          }}
        />
        <Text as="span" size="xs" tone="muted" className="mt-1 block" truncate>
          {file
            ? file.name
            : placeholder || t('no-file-selected') || 'No file selected'}
        </Text>
      </label>
      {error && (
        <Text as="p" size="xs" tone="danger" className="mt-1">
          {error}
        </Text>
      )}
    </div>
  );

  return (
    <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4 sm:p-6">
      <PageHeader
        title={t('account-personalization')}
        caption={
          t('manage-export-templates') ||
          'Manage your export templates and customization settings'
        }
        className="border-b-0 mb-4"
        contentClassName="px-0 py-0"
      >
        <Button
          icon={<Plus width={14} />}
          label={t('add-template')}
          size="small"
          variant="primary"
          onClick={openAddModal}
        />
      </PageHeader>

      {/* Existing Templates Cards */}
      <div>
        {templates && templates?.result.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...templates.result]
              .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
              .map(renderTemplateCard)}
          </div>
        ) : (
          <EmptyState
            icon={<FileText size={28} />}
            title={t('no-templates-found')}
            subtitle={t('click-add-template-to-create-first')}
          />
        )}
      </div>

      {/* Add Template Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title={t('add-new-template') || 'Add New Template'}
        size="large"
        footerActions={[
          <Button
            key="cancel"
            label={t('cancel') || 'Cancel'}
            size="small"
            variant="secondary"
            onClick={closeAddModal}
          />,
          <Button
            key="save"
            label={t('save-template') || 'Save Template'}
            size="small"
            variant="primary"
            onClick={handleSaveNewTemplate}
            disabled={addIsLoading}
            loading={addIsLoading}
          />
        ]}
      >
        <div className="space-y-4">
          <div>
            <Text as="label" size="sm" weight="medium" className="block mb-1">
              {t('template-name')} *
            </Text>
            <Input
              type="text"
              className="h-9 w-full text-sm rounded-2xl"
              placeholder={t('template-name')}
              value={addFormData.name}
              onChange={e => {
                setAddFormData({ ...addFormData, name: e.target.value });
                if (validationErrors.name) {
                  setValidationErrors({
                    ...validationErrors,
                    name: undefined
                  });
                }
              }}
            />
            {validationErrors.name && (
              <Text as="p" size="xs" tone="danger" className="mt-1">
                {validationErrors.name}
              </Text>
            )}
          </div>

          <div>
            <Text as="label" size="sm" weight="medium" className="block mb-1">
              {t('type')}
            </Text>
            <SelectInput
              value={
                typeOptions.find(
                  opt => opt.value === addFormData.template_type
                ) ?? typeOptions[0]
              }
              className="w-full"
              onChange={(opt: OptionType) => {
                const newType = opt.value;
                setAddFormData({
                  ...addFormData,
                  template_type: newType,
                  prompt: getDefaultPromptFor(newType)
                });
              }}
              options={typeOptions}
            />
          </div>

          <div>
            <Text as="label" size="sm" weight="medium" className="block mb-1">
              {t('prompt')}
            </Text>
            <textarea
              className="w-full min-h-[160px] p-2.5 text-sm rounded-2xl border border-hairline bg-app-soft resize-y focus:ring-2 focus:ring-primary/20 focus:border-0 transition-colors"
              placeholder={
                t('enter-prompt') ||
                'Enter custom prompt for document generation. Leave empty to use the default system prompt.'
              }
              value={addFormData.prompt || ''}
              onChange={e =>
                setAddFormData({ ...addFormData, prompt: e.target.value })
              }
            />
            <Text as="p" size="xs" tone="muted" className="mt-1">
              {t('prompt-edit-hint') ||
                'Enter a custom prompt for document generation. Leave empty to use the default system prompt. The prompt will be used to guide the AI in generating documents from transcripts.'}
            </Text>
          </div>

          {addFormData.template_type === 'EXPORT_TEMPLATE' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderUploadField({
                id: 'add_template_docx',
                accept: '.docx',
                file: addFormData.template_docx,
                label: t('upload-docx') || 'Upload DOCX',
                error: validationErrors.template_docx,
                onChange: file => {
                  setAddFormData({ ...addFormData, template_docx: file });
                  if (validationErrors.template_docx) {
                    setValidationErrors({
                      ...validationErrors,
                      template_docx: undefined
                    });
                  }
                }
              })}

              {renderUploadField({
                id: 'add_template_example',
                accept: '.pdf',
                file: addFormData.template_example,
                label: t('upload-pdf') || 'Upload PDF',
                error: validationErrors.template_example,
                onChange: file => {
                  setAddFormData({ ...addFormData, template_example: file });
                  if (validationErrors.template_example) {
                    setValidationErrors({
                      ...validationErrors,
                      template_example: undefined
                    });
                  }
                }
              })}
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Template Modal */}
      <Modal
        isOpen={!!editingTemplate && !!editFormData}
        onClose={closeEditModal}
        title={t('edit-template') || 'Edit Template'}
        size="large"
        footerActions={[
          <Button
            key="cancel"
            label={t('cancel') || 'Cancel'}
            size="small"
            variant="secondary"
            onClick={closeEditModal}
          />,
          <Button
            key="save"
            label={t('save-changes') || 'Save Changes'}
            size="small"
            variant="primary"
            onClick={handleUpdateTemplate}
            disabled={updateIsLoading}
            loading={updateIsLoading}
          />
        ]}
      >
        {editFormData && (
          <div className="space-y-4">
            <div>
              <Text as="label" size="sm" weight="medium" className="block mb-1">
                {t('template-name')} *
              </Text>
              <Input
                type="text"
                className="h-9 w-full text-sm rounded-2xl"
                placeholder={t('template-name')}
                value={editFormData.name}
                onChange={e =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
              />
            </div>

            <div>
              <Text as="label" size="sm" weight="medium" className="block mb-1">
                {t('type')}
              </Text>
              <SelectInput
                value={
                  typeOptions.find(
                    opt => opt.value === editFormData.template_type
                  ) ?? typeOptions[0]
                }
                className="w-full"
                onChange={(opt: OptionType) => {
                  const newType = opt.value;
                  setEditFormData({
                    ...editFormData,
                    template_type: newType,
                    prompt: getDefaultPromptFor(newType)
                  });
                }}
                options={typeOptions}
              />
            </div>

            <div>
              <Text as="label" size="sm" weight="medium" className="block mb-1">
                {t('prompt')}
              </Text>
              <textarea
                className="w-full min-h-[160px] p-2.5 text-sm rounded-2xl border border-hairline bg-app-soft resize-y focus:ring-2 focus:ring-primary/20 focus:border-0 transition-colors"
                placeholder={
                  t('enter-prompt') ||
                  'Enter custom prompt for document generation. Leave empty to use the default system prompt.'
                }
                value={editFormData.prompt || ''}
                onChange={e =>
                  setEditFormData({ ...editFormData, prompt: e.target.value })
                }
              />
              <Text as="p" size="xs" tone="muted" className="mt-1">
                {t('prompt-edit-hint') ||
                  'Enter a custom prompt for document generation. Leave empty to use the default system prompt. The prompt will be used to guide the AI in generating documents from transcripts.'}
              </Text>
            </div>

            {editFormData.template_type === 'EXPORT_TEMPLATE' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderUploadField({
                  id: 'edit_template_docx',
                  accept: '.docx',
                  file: editFormData.template_docx,
                  label: t('upload-docx') || 'Upload DOCX',
                  placeholder:
                    t('optional-leave-empty-to-keep-current') ||
                    'Optional - leave empty to keep current',
                  onChange: file =>
                    setEditFormData({ ...editFormData, template_docx: file })
                })}

                {renderUploadField({
                  id: 'edit_template_example',
                  accept: '.pdf',
                  file: editFormData.template_example,
                  label: t('upload-pdf') || 'Upload PDF',
                  placeholder:
                    t('optional-leave-empty-to-keep-current') ||
                    'Optional - leave empty to keep current',
                  onChange: file =>
                    setEditFormData({ ...editFormData, template_example: file })
                })}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleConfirmDelete}
        size="small"
        title={t('delete-template')}
        caption={t('are-you-sure-you-want-to-delete-this-template')}
        confirmLabel={deleteIsLoading ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={deleteIsLoading}
        destructive
        autoFocusConfirm
      />
    </div>
  );
};

export default AccountPersonalization;
